<template>
  <div v-if="isInFront" class="summarizer">
    <div class="header">
      <div class="header-icon">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      </div>
      <h2>Summarizer</h2>
    </div>

    <div v-if="!hasConversation" class="empty-state">
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        />
      </svg>
      <p>Select a conversation to summarize</p>
    </div>

    <template v-else>
      <div class="conversation-meta" v-if="conversationSubject">
        <span class="meta-label">Conversation</span>
        <span class="meta-value">{{ conversationSubject }}</span>
      </div>

      <div class="section-label">Summarize messages from the past</div>
      <div class="range-picker">
        <div class="number-grid">
          <button
            v-for="n in 12"
            :key="n"
            class="num-btn"
            :class="{ active: selectedAmount === n }"
            @click="selectedAmount = n"
          >
            {{ n }}
          </button>
        </div>
        <div class="unit-grid">
          <button
            v-for="unit in unitOptions"
            :key="unit.value"
            class="unit-btn"
            :class="{ active: selectedUnit === unit.value }"
            @click="selectedUnit = unit.value"
          >
            {{ unit.label }}
          </button>
        </div>
      </div>

      <div class="message-count" v-if="filteredMessages.length > 0">
        <span
          >{{ filteredMessages.length }} message{{
            filteredMessages.length !== 1 ? "s" : ""
          }}
          in range</span
        >
      </div>
      <div class="message-count empty" v-else-if="allMessages.length > 0">
        <span>No messages found in this range</span>
      </div>
      <div v-if="filteredMessages.length > 0" class="filtered-preview">
        <div class="filtered-preview-header">Filtered Messages</div>
        <div class="filtered-preview-body">
          <div
            v-for="m in filteredMessages"
            :key="m.id || m.date"
            class="filtered-msg"
          >
            {{ formatMessage(m) }}
          </div>
        </div>
      </div>

      <button
        class="summarize-btn"
        :class="{ loading: isLoading }"
        :disabled="isLoading || filteredMessages.length === 0"
        @click="summarizeConversation"
      >
        <span v-if="!isLoading" class="btn-content">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
          Generate Summary
        </span>
        <span v-else class="btn-content">
          <span class="spinner"></span>
          Summarizing…
        </span>
      </button>

      <div v-if="error" class="error-box">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        {{ error }}
      </div>

      <div v-if="summary" class="summary-card">
        <div class="summary-header">
          <span class="summary-label">Summary</span>
          <span class="summary-range-tag"
            >Past {{ selectedAmount }} {{ selectedUnitLabel }}</span
          >
        </div>
        <div class="summary-body" v-html="summary"></div>
      </div>
    </template>
  </div>
</template>

<script>
import Front from "@frontapp/plugin-sdk";
import dayjs from "dayjs";

export default {
  name: "ConversationSummarizer",
  data() {
    return {
      isInFront: false,
      hasConversation: false,
      conversationSubject: "",
      allMessages: [],
      selectedAmount: 1,
      selectedUnit: "day",
      unitOptions: [
        { label: "Days", value: "day" },
        { label: "Weeks", value: "week" },
        { label: "Months", value: "month" },
        { label: "Years", value: "year" }
      ],
      isLoading: false,
      summary: "",
      error: ""
    };
  },

  computed: {
    filteredMessages() {
      return this.filterMessages();
    },
    cutoffMs() {
      const msMap = {
        day: 86400000,
        week: 604800000,
        month: 2592000000,
        year: 31536000000
      };
      return msMap[this.selectedUnit] * this.selectedAmount;
    },

    selectedUnitLabel() {
      const unit = this.unitOptions.find(u => u.value === this.selectedUnit);
      const label = unit?.label || "";
      // Singularize if amount is 1
      return this.selectedAmount === 1 ? label.replace(/s$/, "") : label;
    }
  },

  created() {
    try {
      Front.contextUpdates.subscribe(async context => {
        this.isInFront = true;
        this.summary = "";
        this.error = "";

        if (context.type === "singleConversation") {
          this.hasConversation = true;
          this.conversationSubject =
            context.conversation?.subject ||
            `SMS thread with ${context.conversation.recipient.handle}`;

          try {
            this.allMessages = await this.fetchAllMessages(context);
          } catch (e) {
            this.allMessages = [];
            console.error("Failed to list messages:", e);
          }
        } else {
          this.hasConversation = false;
          this.allMessages = [];
          this.conversationSubject = "";
        }
      });
    } catch (e) {
      this.isInFront = false;
    }
  },

  methods: {
    filterMessages() {
      if (!this.allMessages.length) return [];
      const latestMessage = this.allMessages
        .map(m => (m.date ? dayjs(m.date).valueOf() : 0))
        .sort((a, b) => b - a)[0];
      const cutoff = latestMessage - this.cutoffMs;
      return this.allMessages.filter(m => {
        const ts = m.date ? dayjs(m.date).valueOf() : 0;
        return ts >= cutoff;
      });
    },
    stripHtml(html) {
      if (!html) return "";
      return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
    },
    formatMessage(msg) {
      console.log(msg);
      const author = msg.status === "inbound" ? "Customer" : "Caitlin";
      const body = this.stripHtml(msg.content.body) || "";
      const date = msg.date ? new Date(msg.date).toLocaleString() : "";
      return `[${date}] ${author}: ${body}`;
    },
    async fetchAllMessages(context) {
      this.isFetching = true;
      this.fetchedCount = 0;
      const collected = [];

      try {
        let page = await context.listMessages();
        let items = Array.isArray(page) ? page : page?.results ?? [];
        collected.push(...items);
        this.fetchedCount = collected.length;

        while (page?.nextPageToken) {
          page = await context.listMessages({ pageToken: page.nextPageToken });
          items = Array.isArray(page) ? page : page?.results ?? [];
          collected.push(...items);
          this.fetchedCount = collected.length;
        }
      } finally {
        this.isFetching = false;
      }

      return collected;
    },
    buildPrompt() {
      return this.filteredMessages.map(m => this.formatMessage(m)).join("\n\n");
    },

    async summarizeConversation() {
      this.error = "";
      this.summary = "";

      if (!this.filteredMessages.length) {
        this.error = "No messages found in the selected time range.";
        return;
      }

      this.isLoading = true;
      try {
        const prompt = this.buildPrompt();
        const body = JSON.stringify({
          "promptText": prompt,
        })

        const response = await this.axios({
            method: "POST",
            url: `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/ai/message/summarize`,
            body: body,
            headers: {
              Authorization: `Bearer ${this.$store.getters.idToken}`
            }
          });

          this.summary = response.data.message || "";

      } catch (e) {
        this.error = e.message || "Something went wrong. Please try again.";
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&display=swap");

.summarizer {
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 13px;
  color: #1a1a2e;
  padding: 16px;
  min-height: 100vh;
  background: #f7f7f9;
  box-sizing: border-box;
}

/* ── Header ── */
.header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1.5px solid #e2e2ec;
}
.header-icon {
  width: 28px;
  height: 28px;
  background: #0a9ea5;
  color: #fff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.header h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #0a9ea5;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 16px;
  color: #9999b3;
  text-align: center;
}
.empty-state svg {
  opacity: 0.4;
}
.empty-state p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

/* ── Conversation meta ── */
.conversation-meta {
  background: #fff;
  border: 1px solid #e2e2ec;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.meta-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9999b3;
  font-family: "IBM Plex Mono", monospace;
}
.meta-value {
  font-size: 12px;
  font-weight: 500;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Section label ── */
.section-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9999b3;
  font-family: "IBM Plex Mono", monospace;
  margin-bottom: 8px;
}

/* ── Range picker ── */
.range-picker {
  background: #fff;
  border: 1px solid #e2e2ec;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Number grid: 4 columns × 3 rows */
.number-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
}
.num-btn {
  background: #f7f7f9;
  border: 1.5px solid transparent;
  border-radius: 6px;
  padding: 6px 0;
  font-size: 12px;
  font-family: "IBM Plex Mono", monospace;
  font-weight: 500;
  color: #555570;
  cursor: pointer;
  transition: all 0.13s ease;
  text-align: center;
}
.num-btn:hover {
  border-color: #0a9ea5;
  color: #0a9ea5;
}
.num-btn.active {
  background: #0a9ea5;
  border-color: #0a9ea5;
  color: #fff;
}

/* Unit grid: 4 columns */
.unit-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f5;
}
.unit-btn {
  background: #f7f7f9;
  border: 1.5px solid transparent;
  border-radius: 6px;
  padding: 7px 4px;
  font-size: 11px;
  font-family: "IBM Plex Sans", sans-serif;
  font-weight: 600;
  color: #555570;
  cursor: pointer;
  transition: all 0.13s ease;
  text-align: center;
}
.unit-btn:hover {
  border-color: #0a9ea5;
  color: #0a9ea5;
}
.unit-btn.active {
  background: #0a9ea5;
  border-color: #0a9ea5;
  color: #fff;
}

/* ── Message count ── */
.message-count {
  font-size: 11px;
  color: #6b6b8a;
  font-family: "IBM Plex Mono", monospace;
  margin-bottom: 12px;
  padding: 0 2px;
}
.message-count.empty {
  color: #e07b54;
}

/* ── Summarize button ── */
.summarize-btn {
  width: 100%;
  background: #0a9ea5;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 11px 16px;
  font-size: 13px;
  font-family: "IBM Plex Sans", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 14px;
}
.summarize-btn:hover:not(:disabled) {
  background: #088a90;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 158, 165, 0.3);
}
.summarize-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
.spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Error ── */
.error-box {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  background: #fff4f1;
  border: 1px solid #f5cfc5;
  border-radius: 7px;
  padding: 10px 12px;
  font-size: 12px;
  color: #c0422a;
  margin-bottom: 14px;
  line-height: 1.4;
}
.error-box svg {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Summary card ── */
.summary-card {
  background: #fff;
  border: 1.5px solid #e2e2ec;
  border-radius: 10px;
  overflow: hidden;
}
.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f5;
  background: #fafafa;
}
.summary-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9999b3;
  font-family: "IBM Plex Mono", monospace;
}
.summary-range-tag {
  font-size: 10px;
  font-family: "IBM Plex Mono", monospace;
  font-weight: 500;
  background: #0a9ea5;
  color: #fff;
  border-radius: 4px;
  padding: 2px 7px;
}
.summary-body {
  padding: 12px 14px;
  line-height: 1.6;
  color: #2a2a3e;
  font-size: 12.5px;
}
.summary-body :deep(ul) {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.summary-body :deep(li) {
  color: #2a2a3e;
}

.filtered-preview {
  background: #fff;
  border: 1px solid #e2e2ec;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.filtered-preview-header {
  padding: 8px 10px;
  font-size: 10px;
  font-family: "IBM Plex Mono", monospace;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9999b3;
  border-bottom: 1px solid #f0f0f5;
  background: #fafafa;
}

.filtered-preview-body {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow: auto;
}

.filtered-msg {
  font-size: 11px;
  color: #2a2a3e;
  font-family: "IBM Plex Mono", monospace;
  white-space: pre-wrap;
}
</style>